import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../components/AuthContext";

const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!user || !id) return;

    const fetchInvoice = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid, "invoices", id));

        if (snap.exists()) {
          setInvoice(snap.data());
        } else {
          console.warn("Invoice not found");
        }
      } catch (err) {
        console.error("Failed to load invoice:", err);
      }
    };

    fetchInvoice();
  }, [user, id]);

if (loading || !user || !invoice) {
  return <p style={{ padding: 24 }}>Loading...</p>;
}

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Invoice Details</h2>
          <p style={styles.subtitle}>View only mode</p>
        </div>

        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      {/* MAIN CARD */}
      <div style={styles.card}>
        {/* TOP GRID */}
        <div style={styles.grid}>
          <Info label="Invoice Number" value={invoice.invoiceNumber} />
          <Info label="Company" value={invoice.companyName} />
          <Info
            label="Date"
            value={invoice.createdAt?.toDate().toLocaleDateString()}
          />
          <Info
            label="Status"
            value={
              <span
                style={{
                  ...styles.statusBadge,
                  background:
                    invoice.status === "FINAL" ? "#dcfce7" : "#fef9c3",
                  color: invoice.status === "FINAL" ? "#166534" : "#854d0e",
                }}
              >
                {invoice.status || "DRAFT"}
              </span>
            }
          />
        </div>

        {/* ADDRESS SECTION */}
        <div style={styles.addressRow}>
          <div style={styles.addressBox}>
            <h4 style={styles.sectionTitle}>From</h4>
            <p style={styles.addressText}>{invoice.fromAddress}</p>
          </div>

          <div style={styles.addressBox}>
            <h4 style={styles.sectionTitle}>To</h4>
            <p style={styles.addressText}>{invoice.toAddress}</p>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div style={{ marginTop: 24 }}>
          <h3 style={styles.sectionTitle}>Items</h3>

          <table style={styles.table}>
            <thead>
              <tr style={styles.theadRow}>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>HSN</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Rate</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Total</th>
              </tr>
            </thead>

            <tbody>
              {invoice.items?.map((item, idx) => (
                <tr key={idx}>
                  <td style={styles.td}>{item.desc}</td>
                  <td style={styles.td}>{item.hsn}</td>
                  <td style={styles.td}>{item.qty}</td>
                  <td style={styles.td}>₹{item.rate}</td>
                  <td style={styles.td}>{item.unit}</td>
                  <td style={styles.td}>
                    ₹{item.total || item.qty * item.rate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* TOTAL BOX */}
        <div style={styles.totalBox}>
          <div style={styles.totalRow}>
            <span>GST ({invoice.gstPercent}%)</span>
            <strong>₹{invoice.gstAmount?.toFixed(2)}</strong>
          </div>

          <div style={{ ...styles.totalRow, ...styles.grandTotal }}>
            <span>Grand Total</span>
            <strong>₹{invoice.grandTotal?.toFixed(2)}</strong>
          </div>
        </div>

        {/* PAYMENT CONFIRMATION */}
        <div style={styles.paymentCard}>
          <div style={styles.paymentHeader}>
            <h3 style={styles.sectionTitle}>Payment Status</h3>

            <span
              style={{
                ...styles.paymentBadge,
                background:
                  (invoice.payment || "Pending") === "Success"
                    ? "#dcfce7"
                    : "#fef9c3",
                color:
                  (invoice.payment || "Pending") === "Success"
                    ? "#166534"
                    : "#854d0e",
                border:
                  (invoice.payment || "Pending") === "Success"
                    ? "1px solid #22c55e"
                    : "1px solid #facc15",
              }}
            >
              {(invoice.payment || "Pending").toUpperCase()}
            </span>
          </div>

          <div style={styles.paymentInfoGrid}>
            <div style={styles.paymentInfoBox}>
              <p style={styles.paymentLabel}>Payment State</p>
              <p style={styles.paymentValue}>{invoice.payment || "Pending"}</p>
            </div>

            <div style={styles.paymentInfoBox}>
              <p style={styles.paymentLabel}>Grand Total</p>
              <p style={styles.paymentAmount}>
                ₹{invoice.grandTotal?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* NOTES */}
        {invoice.notes && (
          <div style={styles.notesBox}>
            <h4 style={styles.sectionTitle}>Notes</h4>
            <p style={{ color: "#475569" }}>{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div style={styles.infoBox}>
    <p style={styles.infoLabel}>{label}</p>
    <p style={styles.infoValue}>{value || "-"}</p>
  </div>
);

const styles = {
  page: {
    padding: "36px",
    background:
      "linear-gradient(180deg, #f0f6ff 0%, #f8fafc 40%, #ffffff 100%)",
    minHeight: "100vh",
    fontFamily: "Inter, system-ui, -apple-system",
  },

  /* ---------- HEADER ---------- */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 28,
    padding: "18px 22px",
    borderRadius: 16,
    background:
      "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(59,130,246,0.12))",
    border: "1px solid rgba(37,99,235,0.15)",
    backdropFilter: "blur(6px)",
  },

  title: {
    fontSize: 28,
    fontWeight: 800,
    color: "#1e3a8a",
    letterSpacing: "-0.3px",
  },

  subtitle: {
    color: "#64748b",
    fontSize: 14,
    marginTop: 2,
  },

  backBtn: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid #c7d2fe",
    background: "white",
    cursor: "pointer",
    fontWeight: 600,
    color: "#1d4ed8",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(37,99,235,0.12)",
  },

  /* ---------- MAIN CARD ---------- */

  card: {
    background: "rgba(255,255,255,0.9)",
    borderRadius: 22,
    padding: 30,
    boxShadow: "0 25px 60px rgba(2,6,23,0.08)",
    border: "1px solid #e0e7ff",
    backdropFilter: "blur(10px)",
  },

  /* ---------- INFO GRID ---------- */

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
    gap: 20,
    marginBottom: 26,
  },

  infoBox: {
    background: "linear-gradient(135deg, #ffffff, #f8fbff)",
    padding: 16,
    borderRadius: 14,
    border: "1px solid #e6edff",
    boxShadow: "0 6px 18px rgba(37,99,235,0.08)",
  },

  infoLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 6,
    fontWeight: 600,
  },

  infoValue: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },

  /* ---------- ADDRESS ---------- */

  addressRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 22,
    marginTop: 14,
  },

  addressBox: {
    background: "linear-gradient(135deg,#ffffff,#f8fafc)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 8px 22px rgba(2,6,23,0.06)",
  },

  addressText: {
    color: "#334155",
    whiteSpace: "pre-line",
    lineHeight: 1.5,
    fontSize: 14,
  },

  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1e293b",
    letterSpacing: "-0.2px",
  },

  /* ---------- TABLE ---------- */

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    marginTop: 6,
    overflow: "hidden",
    borderRadius: 14,
    border: "1px solid #e2e8f0",
  },

  theadRow: {
    background: "linear-gradient(135deg,#f1f5f9,#eef2ff)",
  },

  th: {
    padding: 14,
    fontSize: 12.5,
    color: "#475569",
    textAlign: "center",
    fontWeight: 700,
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: 14,
    borderBottom: "1px solid #f1f5f9",
    textAlign: "center",
    fontSize: 14,
    color: "#0f172a",
    background: "#ffffff",
  },

  /* ---------- TOTAL BOX ---------- */

  totalBox: {
    marginTop: 26,
    marginLeft: "auto",
    width: 280,
    background: "linear-gradient(135deg,#ffffff,#f8fbff)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e0e7ff",
    boxShadow: "0 12px 28px rgba(37,99,235,0.08)",
  },

  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
    color: "#334155",
    fontSize: 14,
    fontWeight: 500,
  },

  grandTotal: {
    fontSize: 20,
    fontWeight: 800,
    color: "#1d4ed8",
    borderTop: "1px solid #e2e8f0",
    paddingTop: 10,
    marginTop: 6,
  },

  /* ---------- PAYMENT CARD ---------- */

  paymentCard: {
    marginTop: 26,
    background: "linear-gradient(135deg,#ffffff,#f8fbff)",
    padding: 20,
    borderRadius: 18,
    border: "1px solid #e0e7ff",
    boxShadow: "0 14px 32px rgba(37,99,235,0.08)",
  },

  paymentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },

  paymentBadge: {
    padding: "6px 14px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.5px",
  },

  paymentInfoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
    gap: 16,
  },

  paymentInfoBox: {
    background: "#ffffff",
    padding: 14,
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },

  paymentLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 4,
    fontWeight: 600,
  },

  paymentValue: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
  },

  paymentAmount: {
    fontSize: 18,
    fontWeight: 800,
    color: "#1d4ed8",
  },

  /* ---------- STATUS BADGE ---------- */

  statusBadge: {
    padding: "5px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.3px",
  },

  /* ---------- NOTES ---------- */

  notesBox: {
    marginTop: 26,
    background: "linear-gradient(135deg,#ffffff,#f8fafc)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 26px rgba(2,6,23,0.06)",
  },
};

export default ViewInvoice;
